import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { 
  ArrowLeft,
  Save,
  Printer,
  Sparkles,
  FileText,
  BookOpen,
  FileType,
  ClipboardList,
  ScrollText,
  Users,
  Loader2,
  Copy,
  Check,
  Wand2,
  RefreshCw,
  GitBranch,
  Signpost,
  LayoutTemplate,
  ImageIcon
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DOC_TYPES = [
  { id: "memo", label: "Memo", icon: FileText, description: "Professional communications" },
  { id: "training", label: "Training Material", icon: BookOpen, description: "Staff education documents" },
  { id: "quick_reference", label: "Quick Reference", icon: FileType, description: "At-a-glance guides" },
  { id: "checklist", label: "Checklist", icon: ClipboardList, description: "Step-by-step verification" },
  { id: "policy", label: "Policy Summary", icon: ScrollText, description: "Policy overviews" },
  { id: "patient_education", label: "Patient Education", icon: Users, description: "Patient handouts" },
  { id: "workflow", label: "Workflow", icon: GitBranch, description: "Process flow diagrams" },
  { id: "signage", label: "Signage", icon: Signpost, description: "Hospital signs and posters" },
  { id: "a3", label: "A3 Report", icon: LayoutTemplate, description: "Problem-solving A3 format" }
];

export default function DocumentEditor() {
  const { docId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isNew = docId === "new";
  const presetProject = searchParams.get("project");
  const contentRef = useRef(null);
  
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [projects, setProjects] = useState([]);
  const [templates, setTemplates] = useState({});
  const [topics, setTopics] = useState({});
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [copied, setCopied] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  
  const [document, setDocument] = useState({
    title: "",
    doc_type: "training",
    content: "",
    project_id: presetProject || "",
    status: "draft",
    tags: [],
    images: []
  });
  
  const [aiRequest, setAiRequest] = useState({
    doc_type: "training",
    topic: "",
    template: "",
    additional_instructions: ""
  });

  const [imageRequest, setImageRequest] = useState({
    prompt: "",
    style: "professional"
  });

  useEffect(() => {
    fetchProjects();
    fetchTemplates();
    fetchTopics();
    fetchLogo();
    if (!isNew) {
      fetchDocument();
    }
  }, [docId]);

  const fetchLogo = async () => {
    try {
      const res = await fetch(`${API_URL}/api/settings/logo`);
      const data = await res.json();
      if (data.exists && data.data) {
        setLogoUrl(`data:${data.content_type};base64,${data.data}`);
      }
    } catch (error) {
      console.error("Failed to fetch logo:", error);
    }
  };

  const fetchDocument = async () => {
    try {
      const res = await fetch(`${API_URL}/api/documents/${docId}`);
      if (!res.ok) throw new Error("Document not found");
      const data = await res.json();
      setDocument(data);
    } catch (error) {
      toast.error("Failed to load document");
      navigate("/documents");
    }
    setLoading(false);
  };

  const fetchProjects = async () => {
    try {
      const res = await fetch(`${API_URL}/api/projects`);
      const data = await res.json();
      setProjects(data);
    } catch (error) {
      console.error("Failed to fetch projects:", error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ai/templates`);
      const data = await res.json();
      setTemplates(data);
    } catch (error) {
      console.error("Failed to fetch templates:", error);
    }
  };

  const fetchTopics = async () => {
    try {
      const res = await fetch(`${API_URL}/api/ai/topics`);
      const data = await res.json();
      setTopics(data);
    } catch (error) {
      console.error("Failed to fetch topics:", error);
    }
  };

  const handleSave = async () => {
    if (!document.title.trim()) {
      toast.error("Please enter a document title");
      return;
    }
    
    setSaving(true);
    try {
      const url = isNew 
        ? `${API_URL}/api/documents`
        : `${API_URL}/api/documents/${docId}`;
      
      const res = await fetch(url, {
        method: isNew ? "POST" : "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(document)
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(isNew ? "Document created" : "Document saved");
        if (isNew && data.id) {
          navigate(`/documents/${data.id}`, { replace: true });
        }
      } else {
        toast.error("Failed to save document");
      }
    } catch (error) {
      toast.error("Failed to save document");
    }
    setSaving(false);
  };

  const handleGenerate = async () => {
    if (!aiRequest.topic.trim()) {
      toast.error("Please enter a topic");
      return;
    }
    
    setGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/ai/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(aiRequest)
      });
      
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.content) {
          setDocument({
            ...document,
            content: data.content,
            doc_type: aiRequest.doc_type,
            title: document.title || aiRequest.topic
          });
          setAiDialogOpen(false);
          toast.success("Content generated successfully!");
        } else {
          toast.error("Failed to generate content");
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to generate content");
      }
    } catch (error) {
      toast.error("Failed to generate content");
    }
    setGenerating(false);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(document.content);
    setCopied(true);
    toast.success("Content copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleGenerateImage = async () => {
    if (!imageRequest.prompt.trim()) {
      toast.error("Please enter an image description");
      return;
    }
    
    setGeneratingImage(true);
    try {
      const res = await fetch(`${API_URL}/api/ai/generate-image`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(imageRequest)
      });
      
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.image_data) {
          const imageUrl = `data:${data.mime_type};base64,${data.image_data}`;
          setGeneratedImage(imageUrl);
          setDocument({
            ...document,
            images: [...(document.images || []), {
              url: imageUrl,
              prompt: imageRequest.prompt,
              created_at: new Date().toISOString()
            }]
          });
          toast.success("Image generated successfully!");
        } else {
          toast.error(data.error || "Failed to generate image");
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || "Failed to generate image");
      }
    } catch (error) {
      console.error("Image generation error:", error);
      toast.error("Failed to generate image");
    }
    setGeneratingImage(false);
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  const currentTypeTemplates = templates[aiRequest.doc_type] || [];

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-slate-900">
              {isNew ? "New Document" : "Edit Document"}
            </h1>
            <p className="text-sm text-slate-500">
              {isNew ? "Create a new document" : "Modify your document"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Dialog open={aiDialogOpen} onOpenChange={setAiDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-[#6A26B5] text-[#6A26B5]" data-testid="ai-generate-btn">
                <Sparkles className="w-4 h-4 mr-2" />
                Generate with AI
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Wand2 className="w-5 h-5 text-[#6A26B5]" />
                  AI Document Generator
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6 mt-4">
                {/* Document Type Selection */}
                <div>
                  <Label className="text-sm font-medium mb-3 block">Document Type</Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {DOC_TYPES.map((type) => {
                      const Icon = type.icon;
                      return (
                        <button
                          key={type.id}
                          onClick={() => setAiRequest({ ...aiRequest, doc_type: type.id, template: "" })}
                          className={`p-3 rounded-lg border-2 text-left transition-all ${
                            aiRequest.doc_type === type.id 
                              ? "border-[#6A26B5] bg-purple-50" 
                              : "border-slate-200 hover:border-slate-300"
                          }`}
                        >
                          <Icon className={`w-5 h-5 mb-1 ${aiRequest.doc_type === type.id ? "text-[#6A26B5]" : "text-slate-500"}`} />
                          <div className="font-medium text-sm">{type.label}</div>
                          <div className="text-xs text-slate-500">{type.description}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Template Selection */}
                {currentTypeTemplates.length > 0 && (
                  <div>
                    <Label className="text-sm font-medium mb-2 block">Template Style (Optional)</Label>
                    <Select 
                      value={aiRequest.template} 
                      onValueChange={(v) => setAiRequest({ ...aiRequest, template: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select a template style..." />
                      </SelectTrigger>
                      <SelectContent>
                        {currentTypeTemplates.map((t) => (
                          <SelectItem key={t.id} value={t.id}>
                            <div>
                              <div className="font-medium">{t.name}</div>
                              <div className="text-xs text-slate-500">{t.description}</div>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Topic Input */}
                <div>
                  <Label className="text-sm font-medium mb-2 block">Topic *</Label>
                  <Input
                    value={aiRequest.topic}
                    onChange={(e) => setAiRequest({ ...aiRequest, topic: e.target.value })}
                    placeholder="e.g., Hand Hygiene Best Practices"
                    data-testid="ai-topic-input"
                  />
                  
                  {/* Suggested Topics */}
                  <div className="mt-3">
                    <Label className="text-xs text-slate-500 mb-2 block">Suggested topics:</Label>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(topics).slice(0, 1).map(([category, topicList]) => (
                        topicList.slice(0, 5).map((topic) => (
                          <Badge
                            key={topic}
                            variant="outline"
                            className="cursor-pointer hover:bg-purple-50 hover:border-[#6A26B5]"
                            onClick={() => setAiRequest({ ...aiRequest, topic })}
                          >
                            {topic}
                          </Badge>
                        ))
                      ))}
                    </div>
                  </div>
                </div>

                {/* Additional Instructions */}
                <div>
                  <Label className="text-sm font-medium mb-2 block">Additional Instructions (Optional)</Label>
                  <Textarea
                    value={aiRequest.additional_instructions}
                    onChange={(e) => setAiRequest({ ...aiRequest, additional_instructions: e.target.value })}
                    placeholder="Any specific requirements or focus areas..."
                    rows={3}
                  />
                </div>

                {/* Generate Button */}
                <Button 
                  onClick={handleGenerate}
                  disabled={generating || !aiRequest.topic.trim()}
                  className="w-full bg-[#6A26B5] hover:bg-[#5a1f9e]"
                  data-testid="generate-content-btn"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate Content
                    </>
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Image Generation Dialog */}
          <Dialog open={imageDialogOpen} onOpenChange={setImageDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-teal-600 text-teal-600" data-testid="image-generate-btn">
                <ImageIcon className="w-4 h-4 mr-2" />
                Generate Image
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <ImageIcon className="w-5 h-5 text-teal-600" />
                  AI Image Generator
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 mt-4">
                <div>
                  <Label className="text-sm font-medium mb-2 block">Image Description</Label>
                  <Textarea
                    value={imageRequest.prompt}
                    onChange={(e) => setImageRequest({ ...imageRequest, prompt: e.target.value })}
                    placeholder="e.g., Hand washing reminder sign with step-by-step icons"
                    rows={3}
                    data-testid="image-prompt-input"
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium mb-2 block">Style</Label>
                  <Select 
                    value={imageRequest.style} 
                    onValueChange={(v) => setImageRequest({ ...imageRequest, style: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="professional">Professional - Clean hospital style</SelectItem>
                      <SelectItem value="simple">Simple - Bold and easy to read</SelectItem>
                      <SelectItem value="detailed">Detailed - Educational poster style</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Suggested prompts */}
                <div>
                  <Label className="text-xs text-slate-500 mb-2 block">Suggested:</Label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      "Hand hygiene reminder sign",
                      "PPE donning/doffing steps poster",
                      "Contact precautions door sign",
                      "Clean hands save lives poster"
                    ].map((suggestion) => (
                      <Badge
                        key={suggestion}
                        variant="outline"
                        className="cursor-pointer hover:bg-teal-50 hover:border-teal-600"
                        onClick={() => setImageRequest({ ...imageRequest, prompt: suggestion })}
                      >
                        {suggestion}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Generated Image Preview */}
                {generatedImage && (
                  <div className="border rounded-lg p-4 bg-slate-50">
                    <Label className="text-sm font-medium mb-2 block">Generated Image</Label>
                    <img 
                      src={generatedImage} 
                      alt="Generated" 
                      className="w-full rounded-lg shadow-sm"
                    />
                  </div>
                )}

                <Button 
                  onClick={handleGenerateImage}
                  disabled={generatingImage || !imageRequest.prompt.trim()}
                  className="w-full bg-teal-600 hover:bg-teal-700"
                  data-testid="generate-image-btn"
                >
                  {generatingImage ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating Image...
                    </>
                  ) : (
                    <>
                      <ImageIcon className="w-4 h-4 mr-2" />
                      Generate Image
                    </>
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          
          <Button variant="outline" onClick={handlePrint}>
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>
          
          <Button 
            onClick={handleSave}
            disabled={saving}
            className="bg-[#6A26B5] hover:bg-[#5a1f9e]"
            data-testid="save-document-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save
          </Button>
        </div>
      </div>

      {/* Editor */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-3">
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div>
                <Label htmlFor="title">Document Title</Label>
                <Input
                  id="title"
                  value={document.title}
                  onChange={(e) => setDocument({ ...document, title: e.target.value })}
                  placeholder="Enter document title..."
                  className="text-lg font-medium"
                  data-testid="document-title-input"
                />
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label htmlFor="content">Content</Label>
                  <Button variant="ghost" size="sm" onClick={handleCopy}>
                    {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                    {copied ? "Copied" : "Copy"}
                  </Button>
                </div>
                <Textarea
                  ref={contentRef}
                  id="content"
                  value={document.content}
                  onChange={(e) => setDocument({ ...document, content: e.target.value })}
                  placeholder="Start writing or generate content with AI..."
                  className="min-h-[400px] font-mono text-sm leading-relaxed"
                  data-testid="document-content-textarea"
                />
              </div>

              {/* Generated Images */}
              {document.images && document.images.length > 0 && (
                <div>
                  <Label className="mb-2 block">Generated Images</Label>
                  <div className="grid grid-cols-2 gap-4">
                    {document.images.map((img, index) => (
                      <div key={index} className="relative group">
                        <img 
                          src={img.url} 
                          alt={img.prompt} 
                          className="w-full rounded-lg border shadow-sm"
                        />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => {
                              const newImages = document.images.filter((_, i) => i !== index);
                              setDocument({ ...document, images: newImages });
                            }}
                          >
                            Remove
                          </Button>
                        </div>
                        <p className="text-xs text-slate-500 mt-1 truncate">{img.prompt}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Document Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Document Type</Label>
                <Select 
                  value={document.doc_type} 
                  onValueChange={(v) => setDocument({ ...document, doc_type: v })}
                >
                  <SelectTrigger data-testid="doc-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DOC_TYPES.map((type) => (
                      <SelectItem key={type.id} value={type.id}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Project</Label>
                <Select 
                  value={document.project_id || "none"} 
                  onValueChange={(v) => setDocument({ ...document, project_id: v === "none" ? "" : v })}
                >
                  <SelectTrigger data-testid="doc-project-select">
                    <SelectValue placeholder="No project" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Project</SelectItem>
                    {projects.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Status</Label>
                <Select 
                  value={document.status} 
                  onValueChange={(v) => setDocument({ ...document, status: v })}
                >
                  <SelectTrigger data-testid="doc-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => setAiDialogOpen(true)}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Regenerate Content
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={handlePrint}
              >
                <Printer className="w-4 h-4 mr-2" />
                Print Document
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Print Preview Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          .print-content, .print-content * {
            visibility: visible;
          }
          .print-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            padding: 40px;
          }
        }
      `}</style>
    </div>
  );
}
